import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config';

// Componente para manejar el callback de Google OAuth
export const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // El backend envía el token como fragment (#token=) para evitar que quede en logs del servidor.
    // Fallback a ?token= para compatibilidad con links cacheados viejos.
    const hashToken = new URLSearchParams(window.location.hash.slice(1)).get('token');
    const token = hashToken || searchParams.get('token');
    if (token) {
      localStorage.setItem('authToken', token);
      setToken(token);
      
      const apiUrl = getApiUrl('/api/auth/me');
      
      fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        return response.json();
      })
      .then(data => {

        if (data.error) {
          // Error manejado por el catch
          setError(data.error);
        } else {
          setUser(data);

          // Verificar si hay joinMatchAfterLogin para redirigir al partido
          const joinData = localStorage.getItem('joinMatchAfterLogin');
          if (joinData) {
            try {
              const { matchId } = JSON.parse(joinData);
              // Don't remove joinMatchAfterLogin here - JoinMatch.tsx reads it for auto-join
              // Navigate to /join/ so the auto-join effect in JoinMatch fires
              navigate(`/join/${matchId}`);
            } catch (parseError) {
              // Error de parseo manejado silenciosamente
              // Si hay error al parsear, usar el flujo normal
              const redirectUrl = localStorage.getItem('redirectAfterAuth');
              if (redirectUrl) {
                localStorage.removeItem('redirectAfterAuth');
                navigate(redirectUrl);
              } else {
                navigate('/home');
              }
            }
          } else {
            // Flujo normal sin join automático
            const redirectUrl = localStorage.getItem('redirectAfterAuth');
            if (redirectUrl) {
              localStorage.removeItem('redirectAfterAuth');
              // Si el redirect es a una página de join-group, navegar ahí para que
              // GroupJoinPage maneje el auto-join (el pendingGroupToken ya está en localStorage)
              navigate(redirectUrl);
            } else {
              // Verificar si hay datos del formulario de landing
              const landingFormData = localStorage.getItem('landingFormData');
              if (landingFormData) {
                navigate('/create-match');
              } else {
                navigate('/home');
              }
            }
          }
        }
      })
      .catch((error) => {
        // Error manejado por el catch
        setError(`Error al obtener información del usuario: ${error.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setError('No se recibió el token de autenticación');
      setLoading(false);
    }
  }, [searchParams, setToken, setUser, navigate]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#181A1B',
        color: 'var(--text-primary)',
        marginLeft: '90px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⚽</div>
          <div>Completando autenticación...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#181A1B',
        color: 'var(--text-primary)',
        marginLeft: '90px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
          <div>Error: {error}</div>
          <button 
            onClick={() => navigate('/home')}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Componente para manejar fallos de autenticación
export const AuthFailure: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const error = searchParams.get('error') || 'Error desconocido';

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#181A1B',
      color: 'var(--text-primary)',
      marginLeft: '90px'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
        <h2>Error de Autenticación</h2>
        <p>No se pudo completar el proceso de autenticación.</p>
        <p><strong>Error:</strong> {error}</p>
        <div style={{ marginTop: '20px' }}>
          <button 
            onClick={() => navigate('/home')}
            style={{
              marginRight: '12px',
              padding: '8px 16px',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Volver al Inicio
          </button>
          <button 
            onClick={() => navigate('/auth')}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--secondary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Intentar Nuevamente
          </button>
        </div>
      </div>
    </div>
  );
}; 